import {
  findNode,
  getNodePath,
  getStorageNodeTypeLabel,
  readSlotCount,
} from './storageTree';
import { logInfo, logWarn } from '../config_and_helpers/logger';
import { resolveStockItemAssociatedUserId } from '../components/Product/stockItemAssociatedUser';

const LOG_CONTEXT = 'storagePlace';

/**
 * Id → node map for O(1) parent walks. Reused when building place options.
 *
 * @param {Array} nodes
 * @returns {Map<string, Object>}
 */
function indexNodesById(nodes) {
  const byId = new Map();
  (Array.isArray(nodes) ? nodes : []).forEach(node => {
    if (!node || node.id == null) return;
    byId.set(String(node.id), node);
  });
  return byId;
}

/**
 * Parent → child ids map for descendant expansion.
 *
 * @param {Array} nodes
 * @returns {Map<string, string[]>}
 */
function indexChildrenByParent(nodes) {
  const childrenByParent = new Map();
  (Array.isArray(nodes) ? nodes : []).forEach(node => {
    if (!node || node.parent_id == null || node.id == null) return;
    const parentKey = String(node.parent_id);
    const siblings = childrenByParent.get(parentKey) || [];
    siblings.push(String(node.id));
    childrenByParent.set(parentKey, siblings);
  });
  return childrenByParent;
}

/**
 * Path names for one node using an id index (no repeated linear finds).
 *
 * @param {Map<string, Object>} byId
 * @param {string|number} nodeId
 * @returns {string}
 */
function pathLabelFromIndex(byId, nodeId) {
  const names = [];
  const seen = new Set();
  let current = byId.get(String(nodeId));
  while (current) {
    const key = String(current.id);
    if (seen.has(key)) break;
    seen.add(key);
    names.unshift(current.name || 'Place');
    if (current.parent_id == null || current.parent_id === '') break;
    current = byId.get(String(current.parent_id));
  }
  return names.join(' / ');
}

/**
 * Label for a lot in the map picker: batch when the lot has one, otherwise
 * the stock-item id.
 *
 * @param {Object|null|undefined} item
 * @returns {string}
 */
export function formatStockItemLotLabel(item) {
  const batch = item?.batch != null ? String(item.batch).trim() : '';
  if (batch) return batch;
  return item?.id != null ? `Lot ${item.id}` : 'Lot';
}

/**
 * First image URL on a contents row or a product, when one is present.
 *
 * @param {Object|null|undefined} source
 * @returns {string}
 */
function firstImageUrl(source) {
  if (!source) return '';
  const direct = source.first_image || source.image_url;
  if (direct) return String(direct);
  const images = source.images;
  if (Array.isArray(images) && images[0]?.image_url) return String(images[0].image_url);
  return '';
}

/**
 * What a filled chart cell shows: product id (to open inventory), image, CAT#, and lot.
 * Accepts fields on the contents row or on a nested `product`.
 *
 * @param {Object|null|undefined} item
 * @param {Object|null|undefined} [product] - Inventory row looked up by CAT# when the contents row is thin
 * @returns {{ productId: number|string|null, catNum: string, lot: string, imageUrl: string, name: string }}
 */
export function readSlotContentDisplay(item, product) {
  const nested = item?.product && typeof item.product === 'object' ? item.product : null;
  const productId = item?.product_id ?? nested?.id ?? product?.id ?? null;
  const catNum = String(item?.cat_num || nested?.cat_num || product?.cat_num || '').trim();
  const name = String(item?.product_name || nested?.name || product?.name || '').trim();
  const imageUrl =
    firstImageUrl(item) || firstImageUrl(nested) || firstImageUrl(product);
  return {
    productId: productId == null || productId === '' ? null : productId,
    catNum,
    lot: formatStockItemLotLabel(item),
    imageUrl,
    name: name || catNum,
  };
}

/**
 * Fields `updateStockItem` needs so a map place does not wipe batch, in-use,
 * expiry, or opened-on. Place keys are added by the caller.
 *
 * @param {Object|null|undefined} item
 * @returns {{ batch: string, openedBy: string, associatedUserId: number|string|null, inUse: boolean, expiry: string, openedOn: string }}
 */
export function stockItemFieldsForPlace(item) {
  return {
    batch: item?.batch || '',
    openedBy: item?.opened_by_name || '',
    associatedUserId: resolveStockItemAssociatedUserId(item),
    inUse: Boolean(item?.in_use),
    expiry: item?.expiry || '',
    openedOn: item?.opened_on || '',
  };
}

/**
 * True when the product has lots and every lot already has a storage place.
 * An empty list is false, so a product with no lots still shows its location text.
 *
 * @param {Array|null|undefined} items
 * @returns {boolean}
 */
export function allStockItemsHaveStorageNode(items) {
  const list = Array.isArray(items) ? items : [];
  if (!list.length) return false;
  return list.every(item => readStockItemPlace(item).storageNodeId != null);
}

/**
 * Read a lot's place off a stock item. Accepts `storage_node_id` or a nested
 * `storage_node` object from product detail.
 *
 * @param {Object|null|undefined} item
 * @returns {{ storageNodeId: string|number|null, slotIndex: number|null }}
 */
export function readStockItemPlace(item) {
  if (!item) return { storageNodeId: null, slotIndex: null };
  let storageNodeId = item.storage_node_id;
  if (storageNodeId == null && item.storage_node != null) {
    storageNodeId =
      typeof item.storage_node === 'object' ? item.storage_node.id : item.storage_node;
  }
  if (storageNodeId == null || storageNodeId === '') {
    storageNodeId = null;
  }
  const rawSlot = item.slot_index;
  const slotIndex =
    rawSlot == null || rawSlot === '' ? null : Number(rawSlot);
  return {
    storageNodeId,
    slotIndex: Number.isInteger(slotIndex) ? slotIndex : null,
  };
}

/**
 * Boxes only.
 *
 * @param {Array} nodes
 * @returns {Array}
 */
export function listStorageBoxes(nodes) {
  return (Array.isArray(nodes) ? nodes : []).filter(node => node && node.kind === 'box');
}

/**
 * Places a lot can sit: any storage node from the lab tree (room, unit, area,
 * level, box). A slotted box still needs a slot at save time.
 *
 * @param {Array} nodes
 * @returns {Array}
 */
export function listStoragePlaces(nodes) {
  return (Array.isArray(nodes) ? nodes : []).filter(node => node && node.id != null);
}

/**
 * Autocomplete options for the place picker. Built once per tree (path labels
 * use an id index so this stays O(n) instead of O(n²) linear finds).
 *
 * @param {Array} nodes
 * @returns {Array<{ value: string, label: string }>}
 */
export function buildStoragePlaceOptions(nodes) {
  const places = listStoragePlaces(nodes);
  const byId = indexNodesById(places);
  return [
    { value: '', label: 'Not placed' },
    ...places.map(option => {
      const path = pathLabelFromIndex(byId, option.id) || option.name || '';
      const typeLabel = getStorageNodeTypeLabel(option);
      return {
        value: String(option.id),
        label: typeLabel ? `${path} (${typeLabel})` : path,
      };
    }),
  ];
}

/**
 * Ids of a node and every descendant, using a shared children index.
 *
 * @param {Map<string, string[]>} childrenByParent
 * @param {string|number} rootId
 * @returns {Set<string>}
 */
function descendantIdSetFromIndex(childrenByParent, rootId) {
  const ids = new Set([String(rootId)]);
  const queue = [String(rootId)];
  while (queue.length) {
    const parentKey = queue.shift();
    const children = childrenByParent.get(parentKey) || [];
    children.forEach(childId => {
      if (ids.has(childId)) return;
      ids.add(childId);
      queue.push(childId);
    });
  }
  return ids;
}

/**
 * Depth of each node (root = 1) from a parent index.
 *
 * @param {Map<string, Object>} byId
 * @returns {Map<string, number>}
 */
function depthByNodeId(byId) {
  const depths = new Map();
  const depthOf = id => {
    const key = String(id);
    if (depths.has(key)) return depths.get(key);
    const node = byId.get(key);
    if (!node) {
      depths.set(key, 0);
      return 0;
    }
    if (node.parent_id == null || node.parent_id === '') {
      depths.set(key, 1);
      return 1;
    }
    const depth = depthOf(node.parent_id) + 1;
    depths.set(key, depth);
    return depth;
  };
  byId.forEach((_, id) => {
    depthOf(id);
  });
  return depths;
}

/**
 * Filter place-picker options by the place name or any ancestor name.
 * Searching a freezer shows its drawers and boxes. Searching a drawer shows
 * that drawer (and boxes under it). Shorter paths sort first so a drawer is
 * not buried under its boxes. "Not placed" stays visible.
 *
 * @param {Array<{ value: unknown, label?: unknown }>} options
 * @param {string} inputValue
 * @param {Array} [nodes] - Full storage tree (rooms and units included)
 * @returns {Array<{ value: unknown, label?: unknown }>}
 */
export function filterStoragePlaceOptions(options, inputValue, nodes = []) {
  const list = Array.isArray(options) ? options : [];
  const query = String(inputValue || '')
    .trim()
    .toLowerCase();
  if (!query) return list;

  const tree = Array.isArray(nodes) ? nodes : [];
  const byId = indexNodesById(tree);
  const childrenByParent = indexChildrenByParent(tree);
  const depths = depthByNodeId(byId);
  const underMatchedAncestor = new Set();
  tree.forEach(node => {
    if (!node || node.id == null) return;
    if (!String(node.name || '').toLowerCase().includes(query)) return;
    descendantIdSetFromIndex(childrenByParent, node.id).forEach(id =>
      underMatchedAncestor.add(id)
    );
  });

  const filtered = list.filter(option => {
    if (option == null) return false;
    if (String(option.value ?? '') === '') return true;
    const id = String(option.value);
    if (underMatchedAncestor.has(id)) return true;
    const label = String(option.label || '').toLowerCase();
    if (label.includes(query)) return true;
    let current = byId.get(id);
    const seen = new Set();
    while (current) {
      if (String(current.name || '').toLowerCase().includes(query)) return true;
      const key = String(current.id);
      if (seen.has(key)) break;
      seen.add(key);
      if (current.parent_id == null || current.parent_id === '') break;
      current = byId.get(String(current.parent_id));
    }
    return false;
  });

  return filtered.slice().sort((left, right) => {
    if (String(left.value ?? '') === '') return -1;
    if (String(right.value ?? '') === '') return 1;
    const depthLeft = depths.get(String(left.value)) || 0;
    const depthRight = depths.get(String(right.value)) || 0;
    if (depthLeft !== depthRight) return depthLeft - depthRight;
    return String(left.label || '').localeCompare(String(right.label || ''), undefined, {
      sensitivity: 'base',
    });
  });
}

/**
 * "Room / Freezer / Shelf / Box" plus "slot N" when the lot has one.
 * Empty string when the lot is not placed.
 *
 * @param {Array} nodes
 * @param {string|number|null} storageNodeId
 * @param {number|null} [slotIndex]
 * @returns {string}
 */
export function formatStoragePlace(nodes, storageNodeId, slotIndex) {
  if (storageNodeId == null || storageNodeId === '') return '';
  const byId = indexNodesById(nodes);
  if (!byId.has(String(storageNodeId))) {
    logWarn(LOG_CONTEXT, 'placed lot has no matching storage node', {
      storageNodeId,
    });
    return 'Unknown place';
  }
  const names = pathLabelFromIndex(byId, storageNodeId);
  if (!names) {
    logWarn(LOG_CONTEXT, 'placed lot has no matching storage node', {
      storageNodeId,
    });
    return 'Unknown place';
  }
  if (slotIndex == null) return names;
  return `${names} · slot ${slotIndex}`;
}

/**
 * "Lot B1" from a batch, otherwise "Lot {id}".
 *
 * @param {{ batch?: string, id?: string|number, stock_item_id?: string|number }|null|undefined} item
 * @returns {string}
 */
function lotLocationHeading(item) {
  const batch = item?.batch != null ? String(item.batch).trim() : '';
  if (batch) return `Lot ${batch}`;
  const id = item?.stock_item_id ?? item?.id;
  if (id != null && id !== '') return `Lot ${id}`;
  return 'Lot';
}

/**
 * One Location line for a lot already loaded on the product.
 * Returns null when the storage tree is not loaded yet, so a placed lot is
 * not shown as unknown.
 *
 * @param {Array|null|undefined} items
 * @param {Array|null|undefined} nodes
 * @returns {string[]|null}
 */
export function formatLotLocationLinesFromItems(items, nodes, productLocation) {
  if (!Array.isArray(nodes)) return null;
  const fallback = String(productLocation || '').trim() || 'Not placed';
  const list = Array.isArray(items) ? items : [];
  return list.map(item => {
    const { storageNodeId, slotIndex } = readStockItemPlace(item);
    const place = formatStoragePlace(nodes, storageNodeId, slotIndex);
    return `${lotLocationHeading(item)} — ${place || fallback}`;
  });
}

/**
 * Lines from the product list field `lot_locations`.
 * Each entry is `{ stock_item_id, batch, place }`.
 *
 * @param {Array<{ batch?: string, stock_item_id?: string|number, place?: string|null }>|null|undefined} lotLocations
 * @returns {string[]}
 */
export function formatLotLocationLinesFromApi(lotLocations) {
  const list = Array.isArray(lotLocations) ? lotLocations : [];
  return list.map(entry => {
    const place = entry?.place != null ? String(entry.place).trim() : '';
    return `${lotLocationHeading(entry)} — ${place || 'Not placed'}`;
  });
}

/** Emoji for a storage unit or place. Matches the kinds on the storage map. */
const STORAGE_PLACE_EMOJI = {
  room: '🚪',
  ult_freezer: '🧊',
  freezer_20: '🧊',
  refrigerator: '❄️',
  ln2_dewar: '🫙',
  incubator: '🌡️',
  cabinet: '🗄️',
  storage_shelves: '🗄️',
  shelf: '🗂️',
  rack: '🗂️',
  drawer: '🧰',
  box: '📦',
};

/**
 * Each place in the path, with its own emoji, so the UI does not join names with "/".
 * A slot stays on the last step.
 *
 * @param {Array} nodes
 * @param {string|number|null} storageNodeId
 * @param {number|null} slotIndex
 * @returns {Array<{ name: string, emoji: string }>}
 */
function storagePlaceSteps(nodes, storageNodeId, slotIndex) {
  if (storageNodeId == null || storageNodeId === '') return [];
  const steps = getNodePath(nodes, storageNodeId).map(node => ({
    name: node?.name || 'Place',
    emoji: STORAGE_PLACE_EMOJI[node?.subtype || node?.kind] || '📍',
  }));
  if (slotIndex != null && steps.length) {
    const last = steps[steps.length - 1];
    steps[steps.length - 1] = { ...last, name: `${last.name} · slot ${slotIndex}` };
  }
  return steps;
}

/**
 * Lots grouped by the place they share. Null when the storage tree is not loaded.
 *
 * @param {Array|null|undefined} items
 * @param {Array|null|undefined} nodes
 * @param {string|null|undefined} [productLocation] - Shown for lots that are not in the storage tree
 * @returns {Array<{ place: string, lots: string[], steps: Array<{ name: string, emoji: string }> }>|null}
 */
export function groupLotLocations(items, nodes, productLocation) {
  if (!Array.isArray(nodes)) return null;
  const fallback = String(productLocation || '').trim() || 'Not placed';
  const list = Array.isArray(items) ? items : [];
  const order = [];
  const byPlace = new Map();
  list.forEach(item => {
    const { storageNodeId, slotIndex } = readStockItemPlace(item);
    const placed = formatStoragePlace(nodes, storageNodeId, slotIndex);
    const place = placed || fallback;
    if (!byPlace.has(place)) {
      byPlace.set(place, {
        lots: [],
        steps: placed ? storagePlaceSteps(nodes, storageNodeId, slotIndex) : [],
      });
      order.push(place);
    }
    byPlace.get(place).lots.push(lotLocationHeading(item));
  });
  return order.map(place => ({
    place,
    lots: byPlace.get(place).lots,
    steps: byPlace.get(place).steps,
  }));
}

/**
 * Short Location summary. One shared place uses its last name. Several places
 * use a count.
 *
 * @param {Array<{ place: string, lots: string[] }>|null|undefined} groups
 * @returns {string|null}
 */
export function summarizeLotLocations(groups) {
  if (!groups) return null;
  if (!groups.length) return '—';
  const lotCount = groups.reduce((total, group) => total + group.lots.length, 0);
  if (groups.length === 1) {
    const parts = String(groups[0].place).split(' / ');
    const shortName = parts[parts.length - 1] || groups[0].place;
    return lotCount === 1 ? shortName : `${shortName} · ${lotCount} lots`;
  }
  const lotWord = lotCount === 1 ? 'lot' : 'lots';
  const placeWord = groups.length === 1 ? 'place' : 'places';
  return `${lotCount} ${lotWord} · ${groups.length} ${placeWord}`;
}

/**
 * Validate a place before save. Any tree node is allowed. A slotted box
 * requires a slot. Every other kind (room, unit, area, level, plain box)
 * sends `slot_index: null` and rejects a non-null slot. Clearing the place
 * clears the slot.
 *
 * @param {Array} nodes
 * @param {string|number|null|undefined} storageNodeId
 * @param {string|number|null|undefined} slotIndex
 * @returns {{ storage_node_id: string|number|null, slot_index: number|null, error: string|null }}
 */
export function parseStoragePlace(nodes, storageNodeId, slotIndex) {
  if (storageNodeId == null || storageNodeId === '') {
    return { storage_node_id: null, slot_index: null, error: null };
  }
  const place = findNode(nodes, storageNodeId);
  if (!place) {
    return {
      storage_node_id: null,
      slot_index: null,
      error: 'Choose a storage place',
    };
  }
  const hasSlot = slotIndex != null && slotIndex !== '';
  const slots = readSlotCount(place);
  if (!slots) {
    if (hasSlot) {
      return {
        storage_node_id: place.id,
        slot_index: null,
        error: 'That place has no slots',
      };
    }
    logInfo(LOG_CONTEXT, 'lot placed on an unslotted node', {
      storageNodeId: place.id,
      kind: place.kind ?? null,
      subtype: place.subtype ?? null,
    });
    return { storage_node_id: place.id, slot_index: null, error: null };
  }
  const count = hasSlot ? Number(slotIndex) : NaN;
  if (!Number.isInteger(count) || count < 1 || count > slots) {
    return { storage_node_id: place.id, slot_index: null, error: 'Choose a slot' };
  }
  logInfo(LOG_CONTEXT, 'lot placed in a slotted box', {
    storageNodeId: place.id,
    slotIndex: count,
  });
  return { storage_node_id: place.id, slot_index: count, error: null };
}

/**
 * Slots for a move of one or more lots. One lot into a slotted box uses the
 * chosen slot. Several lots into a slotted box take the next free slots.
 * Any other node (room, unit, area, level, plain box) uses no slot. Not
 * enough free slots refuses the move before any save.
 *
 * @param {Array} nodes
 * @param {string|number|null|undefined} storageNodeId
 * @param {string|number|null|undefined} slotIndex
 * @param {number} lotCount
 * @param {Array<number|null|undefined>} [occupiedSlots]
 * @returns {{ error: string|null, storageNodeId: string|number|null, slots: Array<number|null> }}
 */
export function planLotMoves(nodes, storageNodeId, slotIndex, lotCount, occupiedSlots = []) {
  const count = Number(lotCount) || 0;
  if (count < 1) {
    return { error: 'Select a lot', storageNodeId: null, slots: [] };
  }
  const place = findNode(nodes, storageNodeId);
  if (!place) {
    return {
      error: 'Choose a storage place',
      storageNodeId: null,
      slots: [],
    };
  }
  const capacity = readSlotCount(place);
  if (!capacity) {
    if (slotIndex != null && slotIndex !== '') {
      return { error: 'That place has no slots', storageNodeId: place.id, slots: [] };
    }
    logInfo(LOG_CONTEXT, 'lots planned onto an unslotted place', {
      storageNodeId: place.id,
      kind: place.kind ?? null,
      lotCount: count,
    });
    return {
      error: null,
      storageNodeId: place.id,
      slots: Array.from({ length: count }, () => null),
    };
  }
  if (count === 1) {
    const parsed = parseStoragePlace(nodes, storageNodeId, slotIndex);
    if (parsed.error) {
      return { error: parsed.error, storageNodeId: place.id, slots: [] };
    }
    return { error: null, storageNodeId: place.id, slots: [parsed.slot_index] };
  }
  const taken = new Set(
    (occupiedSlots || []).map(slot => Number(slot)).filter(slot => Number.isInteger(slot))
  );
  const free = [];
  for (let index = 1; index <= capacity; index += 1) {
    if (!taken.has(index)) free.push(index);
  }
  if (free.length < count) {
    const freeLabel = free.length === 1 ? '1 slot is free' : `${free.length} slots are free`;
    logInfo(LOG_CONTEXT, 'not enough free slots for the move', {
      storageNodeId: place.id,
      lotCount: count,
      freeCount: free.length,
    });
    return { error: freeLabel, storageNodeId: place.id, slots: [] };
  }
  logInfo(LOG_CONTEXT, 'lots planned into free slots', {
    storageNodeId: place.id,
    lotCount: count,
    slots: free.slice(0, count),
  });
  return { error: null, storageNodeId: place.id, slots: free.slice(0, count) };
}
